import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, inject, Injector, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAvatarComponent,
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxToastService,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { CommonHelper } from '../../../../../../common/helpers/common.helper';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { FleetHelper } from '../../../../../fleet/common/helpers/fleet.helper';
import type { Fleet } from '../../../../../fleet/common/interfaces/fleet';
import type { Employee } from '../../interfaces/employee';
import { EmployeesService } from '../../services/employees.service';

export type EmployeeAssignVehiclesModalData = Employee;

@Component({
  selector: 'app-employee-assign-vehicles-modal',
  imports: [
    OnyxModalComponent,
    OnyxDropdownComponent,
    ReactiveFormsModule,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxAvatarComponent,
    OnyxNamePipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './employee-assign-vehicles-modal.component.html',
  styleUrl: './employee-assign-vehicles-modal.component.scss',
})
export class EmployeeAssignVehiclesModalComponent {
  protected readonly employee =
    inject<EmployeeAssignVehiclesModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly employeesService = inject(EmployeesService);
  private readonly fleetHelper = inject(FleetHelper);

  protected readonly I18N = 'employees.assignVehiclesModal';
  protected readonly VEHICLES_SOURCE = this.fleetHelper.getVehiclesSource();

  protected form = this.buildForm();
  protected loading = signal(false);
  protected vehicle = signal<Fleet | null>(null);
  protected vehicleLink = computed(() =>
    CommonHelper.getPreviewLink(this.vehicle(), (vehicle) =>
      this.fleetHelper.openModal(vehicle),
    ),
  );
  protected close$ = new Subject<void>();

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    this.loading.set(true);
    this.employeesService
      .assignVehicles(this.employee.uuid, form.vehicles)
      .subscribe(() => {
        this.toastService.showSuccess({
          message: `${this.I18N}.assignedVehicles`,
        });
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      vehicles: this.fb.control<string[]>(this.employee.assignedVehicles),
    });
  }
}
